import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import Headline6 from '../Typography/Heading/Headline6';
import Overline from '../Typography/Overline';
import { List } from '.';
import breakpoints from '../Theme/breakpoints';
import Skeleton from '../SkeletonLoader/Skeleton';
import grey from '../Palettes/grey';
import overlay from '../Palettes/overlay';

const MostReadWrapper = styled.div`
  border-bottom: 1px solid ${grey[325]};
  padding: 0 0 12px;
`;

const MostReadHeading = styled(Headline6)`
  font-size: 22px;
  padding-bottom: 12px;
`;

const MostReadListWrapper = styled(List)`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-around;
  & > * {
    flex: 0 0 100%;
  }
  @media (min-width: ${breakpoints.sm}px) {
    justify-content: flex-start;
    && > * {
      flex: 0 0 48%;
      padding-right: 4%;
      &:nth-of-type(even) {
        padding-right: 0;
      }
    }
  }
  @media (min-width: ${breakpoints.md}px) {
    && > * {
      flex: 0 0 30%;
      &, &:nth-of-type(2), &:nth-of-type(4) {
        padding-right: 5%;
      }
      &:nth-of-type(3n) {
        padding-right: 0;
      }
    }
  }
`;

const mostReadContainerPropertyTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

type MostReadContainerProperties = InferProps<
  typeof mostReadContainerPropertyTypes
>;

export const MostReadContainer = ({
  title,
  children,
}: MostReadContainerProperties) => (
  <MostReadWrapper>
    <MostReadHeading tag="h2">
      {title}
    </MostReadHeading>
    <MostReadListWrapper resetpadding>
      { children }
    </MostReadListWrapper>
  </MostReadWrapper>
);

MostReadContainer.propTypes = mostReadContainerPropertyTypes;

const MostReadItemContainer = styled.div`
  &&& {
    display: flex;
  }
  padding: 4px 0 4px;
  min-height: 2rem;
  @media (min-width: ${breakpoints.md}px) {
    padding: 16px 0;
    border-top: none;
    min-height: none;
  }
`;

const NumberWrapper = styled.div`
  background-color: #E8E8E8;
  color: ${overlay[600]};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  align-self: flex-start;
  margin: 0 12px 0 0;
  width: 22px;
  height: 22px;
  border-radius: 12px;
  font-size: 14px;

  @media (min-width: ${breakpoints.md}px) {
    margin: 0 20px 0 0;
    width: 44px;
    height: 44px;
    border-radius: 22px;
    font-size: 18px;
  }
`;

const MostReadHeadline = styled(Headline6)`
  && {
    font-size: 14px;
    line-height: 1.2rem;
  }
  word-break: break-word;
  padding: 2px 0 0;
  @media (min-width: ${breakpoints.md}px) {
    flex-grow: 1;
    padding: 0 0 4px;
    && {
      font-size: 1.125rem;
      line-height: 1.5rem;
    }
  }
`;

const ArticleCategory = styled(Overline)`
  display: none;
  @media (min-width: ${breakpoints.md}px) {
    && {
      display: inline-block;
      padding-right: 0.4rem;
    }
  }
`;

const ArticleDate = styled.span`
  display: none;
  font-family: ${({ theme }) => theme.fonts.article};
  color: ${overlay[400]};
  font-size: 14px;

  @media (min-width: ${breakpoints.md}px) {
    && {
      display: inline-block;
    }
  }
`;

const mostReadItemPropertyTypes = {
  title: PropTypes.string.isRequired,
  date: PropTypes.node,
  number: PropTypes.number.isRequired,
  category: PropTypes.string,
  showLoader: PropTypes.bool,
};

type MostReadItemProperties = InferProps<
  typeof mostReadItemPropertyTypes
>;

const MostReadItem = ({
  title,
  date,
  number,
  category,
  showLoader,
}: MostReadItemProperties) => (
  <MostReadItemContainer>
    {showLoader ? (
      <NumberWrapper>
        <Skeleton variant="circular" width="100%" height="44px" />
      </NumberWrapper>
    ) : <NumberWrapper>{number}</NumberWrapper>}
    <div>
      {showLoader ? (
        <MostReadHeadline>
          <Skeleton variant="rectangular" width="100%" />
        </MostReadHeadline>
      ) : title && <MostReadHeadline tag="h3">{title}</MostReadHeadline>}
      {showLoader ? (
        <ArticleCategory>
          <Skeleton variant="rectangular" width="52px">&nbsp;</Skeleton>
        </ArticleCategory>
      ) : category && <ArticleCategory>{category}</ArticleCategory>}
      {showLoader ? (
        <ArticleDate>
          <Skeleton variant="rectangular" width="72px">&nbsp;</Skeleton>
        </ArticleDate>
      ) : date && <ArticleDate>{date}</ArticleDate>}
    </div>
  </MostReadItemContainer>
);

MostReadItem.propTypes = mostReadItemPropertyTypes;

export default MostReadItem;
